import React from "react";

import {
  FaBookOpen,
  FaGlobeEurope,
  FaPlus,
  FaRocket,
  FaSave,
  FaSyncAlt,
} from "react-icons/fa";

import {
  ToastContainer,
} from "react-toastify";

import "react-toastify/dist/ReactToastify.css";

import {
  useNavigate,
} from "react-router-dom";

import useScrollToTop from "../../../../hooks/useScrollToTop";

import useTutorialsAdmin from "../hooks/useTutorialsAdmin";

import TutorialCard from "../components/TutorialCard";
import TutorialFilters from "../components/TutorialFilters";
import TutorialFormDrawer from "../components/TutorialFormDrawer";
import ConfirmTutorialDialog from "../components/ConfirmTutorialDialog";

const HERO =
  "relative overflow-hidden rounded-[30px] border border-slate-800 bg-slate-950 text-white shadow-[0_25px_70px_-25px_rgba(15,23,42,0.85)]";

export default function TutorialsAdminPage() {
  useScrollToTop();

  const navigate = useNavigate();

  const {
    displayedTutorials,
    statistics,

    loading,
    refreshing,
    actionLoading,

    search,
    setSearch,

    statusFilter,
    setStatusFilter,

    scopeFilter,
    setScopeFilter,

    sortBy,
    setSortBy,

    drawerState,
    openCreateDrawer,
    openEditDrawer,
    closeDrawer,
    saveTutorial,

    confirmation,
    requestPublish,
    requestDuplicate,
    requestDelete,
    closeConfirmation,
    confirmRequestedAction,

    loadTutorials,
    resetFilters,
  } = useTutorialsAdmin();

  const openTutorialEditor = (tutorial) => {
    navigate(
      `/appli/tutorials/${tutorial.id}`
    );
  };

  return (
    <div className="space-y-6 p-4 lg:p-6">
      <section className={HERO}>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(99,102,241,0.40),transparent_32%),radial-gradient(circle_at_top_right,rgba(217,70,239,0.30),transparent_28%),radial-gradient(circle_at_bottom_left,rgba(16,185,129,0.15),transparent_30%)]" />

        <div className="relative p-6 md:p-8">
          <div className="flex flex-col gap-6 xl:flex-row xl:items-start xl:justify-between">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-white/80">
                <FaBookOpen />
                Centre de formation
              </div>

              <h1 className="mt-4 text-3xl font-semibold tracking-tight md:text-4xl">
                Tutoriels dynamiques
              </h1>

              <p className="mt-3 max-w-2xl text-sm leading-6 text-white/75 md:text-base">
                Crée, versionne et publie les
                tutoriels partagés avec l’ensemble
                de tes applications et sites
                clients.
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={openCreateDrawer}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-4 py-2.5 text-sm font-semibold text-slate-900 transition hover:bg-slate-100"
              >
                <FaPlus />
                Nouveau tutoriel
              </button>

              <button
                type="button"
                disabled={refreshing}
                onClick={() =>
                  loadTutorials({
                    silent: true,
                  })
                }
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/15 bg-white/10 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-white/20 disabled:opacity-50"
              >
                <FaSyncAlt
                  className={
                    refreshing
                      ? "animate-spin"
                      : ""
                  }
                />

                Rafraîchir
              </button>
            </div>
          </div>

          <div className="mt-7 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <StatCard
              icon={<FaBookOpen />}
              label="Tutoriels"
              value={statistics.total}
              hint="Toutes les versions"
            />

            <StatCard
              icon={<FaRocket />}
              label="Publiés"
              value={statistics.published}
              hint="Versions actuellement actives"
            />

            <StatCard
              icon={<FaSave />}
              label="Brouillons"
              value={statistics.drafts}
              hint="Versions en préparation"
            />

            <StatCard
              icon={<FaGlobeEurope />}
              label="Personnalisés"
              value={statistics.siteSpecific}
              hint="Tutoriels propres à un site"
            />
          </div>
        </div>
      </section>

      <TutorialFilters
        search={search}
        onSearchChange={setSearch}
        statusFilter={statusFilter}
        onStatusFilterChange={
          setStatusFilter
        }
        scopeFilter={scopeFilter}
        onScopeFilterChange={
          setScopeFilter
        }
        sortBy={sortBy}
        onSortChange={setSortBy}
        onReset={resetFilters}
      />

      <section>
        <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-violet-500">
              Versions disponibles
            </div>

            <h2 className="mt-1 text-xl font-semibold text-slate-900 dark:text-slate-50">
              Tutoriels enregistrés
            </h2>
          </div>

          <div className="rounded-full bg-violet-100 px-3 py-1 text-sm font-semibold text-violet-700 dark:bg-violet-500/15 dark:text-violet-300">
            {displayedTutorials.length} résultat(s)
          </div>
        </div>

        {loading ? (
          <TutorialSkeleton />
        ) : displayedTutorials.length ===
          0 ? (
          <EmptyTutorialState
            onCreate={openCreateDrawer}
          />
        ) : (
          <div className="grid grid-cols-1 gap-5 xl:grid-cols-2 2xl:grid-cols-3">
            {displayedTutorials.map(
              (tutorial) => (
                <TutorialCard
                  key={tutorial.id}
                  tutorial={tutorial}
                  busy={Boolean(
                    actionLoading?.endsWith(
                      `-${tutorial.id}`
                    )
                  )}
                  onOpen={
                    openTutorialEditor
                  }
                  onEdit={openEditDrawer}
                  onPublish={
                    requestPublish
                  }
                  onDuplicate={
                    requestDuplicate
                  }
                  onDelete={
                    requestDelete
                  }
                />
              )
            )}
          </div>
        )}
      </section>

      <TutorialFormDrawer
        open={drawerState.open}
        mode={drawerState.mode}
        tutorial={
          drawerState.tutorial
        }
        saving={Boolean(
          actionLoading === "create" ||
            actionLoading?.startsWith(
              "update-"
            )
        )}
        onClose={closeDrawer}
        onSave={saveTutorial}
      />

      <ConfirmTutorialDialog
        confirmation={confirmation}
        loading={Boolean(
          confirmation &&
            actionLoading ===
              `${confirmation.type}-${confirmation.tutorial?.id}`
        )}
        onClose={closeConfirmation}
        onConfirm={
          confirmRequestedAction
        }
      />

      <ToastContainer />
    </div>
  );
}

function StatCard({
  icon,
  label,
  value,
  hint,
}) {
  return (
    <div className="rounded-[22px] border border-white/15 bg-white/10 p-4 backdrop-blur-md">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-white/60">
            {label}
          </div>

          <div className="mt-2 text-2xl font-semibold text-white">
            {value}
          </div>

          <div className="mt-1 text-xs text-white/65">
            {hint}
          </div>
        </div>

        <div className="rounded-2xl bg-white/10 p-3 text-white">
          {icon}
        </div>
      </div>
    </div>
  );
}

function TutorialSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-5 xl:grid-cols-2 2xl:grid-cols-3">
      {Array.from({
        length: 6,
      }).map((_, index) => (
        <div
          key={index}
          className="h-[390px] animate-pulse rounded-[26px] bg-slate-200 dark:bg-slate-800"
        />
      ))}
    </div>
  );
}

function EmptyTutorialState({
  onCreate,
}) {
  return (
    <div className="rounded-[28px] border border-dashed border-slate-300 bg-white p-10 text-center dark:border-slate-700 dark:bg-slate-900">
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-[22px] bg-violet-100 text-2xl text-violet-600 dark:bg-violet-500/15 dark:text-violet-300">
        <FaBookOpen />
      </div>

      <h3 className="mt-5 text-xl font-semibold text-slate-900 dark:text-slate-50">
        Aucun tutoriel trouvé
      </h3>

      <p className="mx-auto mt-2 max-w-lg text-sm leading-6 text-slate-500 dark:text-slate-400">
        Modifie les filtres ou crée une
        nouvelle version du tutoriel.
      </p>

      <button
        type="button"
        onClick={onCreate}
        className="mt-5 inline-flex items-center justify-center gap-2 rounded-2xl bg-violet-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-violet-700"
      >
        <FaPlus />
        Créer un tutoriel
      </button>
    </div>
  );
}